goog.declareModuleId('yext.ui.components.filters.filterbar');


import {GRAY2, GRAY3, GRAY7, WHITE} from '/ui/colors/colors';
import {DeprecatedOldButton as Button} from '/ui/components/button/button';
import {DropdownTrigger} from '/ui/components/dropdowntrigger/dropdowntrigger';
import {Pill} from '/ui/components/filters/filterpill';
import {SelectDropdownContent} from '/ui/components/select/root/selectdropdowncontent';
import {Tooltip} from '/ui/components/tooltip/tooltip';
import {classNames} from '/ui/lib/classnames';
import {yext} from '/ui/lib/msg';

import * as styles from '/ui/components/filters/filterbar.module.scss';

/**
 * @typedef {{
 *   children?: React.ReactNode,
 *   disableBorder?: boolean,
 * }}
 */
export let SectionProps;

/**
 * @param {SectionProps} props
 * @return {React.ReactElement}
 */
export function Section({children, disableBorder = false}) {
  return (
    <div className={`${styles.section} ${disableBorder ? styles.disableBorder : ''}`}>
      {children}
    </div>
  );
}

export function RightSection({children}) {
  return (
    <div className={styles.rightSection}>
      {children}
    </div>
  );
}


/**
 * @param {{
 *   filters: !Array<!{
 *     callback: (function():void),
 *     name: string,
 *   }>,
 *   manageSavedFiltersUrl: string,
 *   canManageSavedFilters?: boolean,
 *   selectedSavedFilterName?: ?string,
 *   useIconTrigger?: boolean,
 *   pendoId?: string,
 * }} props
 */
export function SavedFilterButton({
  filters,
  manageSavedFiltersUrl,
  canManageSavedFilters = true,
  selectedSavedFilterName = null,
  useIconTrigger = false,
  pendoId,
}) {
  const [dropdownExpanded, setDropdownExpanded] = React.useState(false);
  const [filtersSearchTerm, setFiltersSearchTerm] = React.useState('');
  const buttonRef = React.useRef(null);

  const getOptions = filters =>
    filters.map(filter => ({
      value: filter.name,
      label: filter.name,
    }),
    );

  const [selectableOptions, setSelectableOptions] = React.useState(getOptions(filters));
  const [options, setOptions] = React.useState(getOptions(filters));

  React.useEffect(() => {
    const newOptions = getOptions(filters);
    setOptions(newOptions);
  }, [filters]);

  React.useEffect(() => {
    if (filtersSearchTerm) {
      setSelectableOptions(options.filter(
        option => option.label.toLowerCase().includes(filtersSearchTerm.toLowerCase())),
      );
    } else {
      setSelectableOptions(options);
    }
  }, [filtersSearchTerm, options]);

  const onCloseDropdown = () => {
    setDropdownExpanded(false);
    setFiltersSearchTerm('');
  };
  const onToggleDropdown = () => {
    setDropdownExpanded(!dropdownExpanded);
    setFiltersSearchTerm('');
  };

  const content = (
    <SelectDropdownContent
      options={selectableOptions}
      multiselect={false} // Do not need to provide onOptionChange since multiselect is false
      setFocusOnSelectChange={([optionVal]) => {
        const selectedFilter = filters.find(filter => filter.name === optionVal);
        if (selectedFilter) {
          selectedFilter.callback();
          onCloseDropdown();
        } else {
          console.warn(`SavedFilterButton: filter not found for val ${optionVal}`);
        }
      }}
      onCloseDropdown={onCloseDropdown}
      allowSearch={true}
      searchTerm={filtersSearchTerm}
      onSearch={setFiltersSearchTerm}
      actionLink={canManageSavedFilters ? {
        label: yext.msg('Manage Filters'),
        path: manageSavedFiltersUrl,
        pendoId: pendoId && `${pendoId}-manage-filters`,
      } : undefined}
      isVisible={dropdownExpanded}
    />
  );

  const triggerContent = function(dropdownExpanded) {
    return useIconTrigger ? (
      <Tooltip tooltipText={yext.msg('Apply Saved Filter')}>
        <div className={styles.triggerContent}>
          <span className="icon-filter-hopper"/>
          {!selectedSavedFilterName ? null : selectedSavedFilterName}
        </div>
      </Tooltip>
    ) : (
      <>
        {selectedSavedFilterName || yext.msg('Saved Filters')}
        <span className={dropdownExpanded ? 'icon-arrow-up-gray' : 'icon-arrow-down-gray'}/>
      </>
    );
  };

  const Trigger = function({dropdownExpanded, buttonRef, attributes}) {
    const [useHoverStyle, setUseHoverStyle] = React.useState(false);
    const triggerClass = classNames(
      attributes.className,
      styles.savedFilterButtonTrigger,
      {
        [styles.triggerHover]: useHoverStyle,
        [styles.iconTrigger]: useIconTrigger,
      },
    );
    return (
      <button
        {...attributes}
        ref={buttonRef}
        type="button"
        className={triggerClass}
        id="saved-filters-dropdown-button"
        tid="saved-filters-dropdown-button"
        onFocus={() => useIconTrigger && setUseHoverStyle(true)}
        onMouseOver={() => useIconTrigger && setUseHoverStyle(true)}
        onBlur={() => useIconTrigger && setUseHoverStyle(false)}
        onMouseOut={() => useIconTrigger && setUseHoverStyle(false)}
      >
        {triggerContent(dropdownExpanded)}
      </button>
    );
  };

  return (
    <DropdownTrigger
      ref={buttonRef}
      onToggleDropdown={onToggleDropdown}
      onCloseDropdown={onCloseDropdown}
      dropdownExpanded={dropdownExpanded}
      focusOutDetection={'strong'}
      alignMenu={'left'}
      pendoId={pendoId}
      trigger={Trigger}
    >
      {content}
    </DropdownTrigger>
  );
}

/**
 * @typedef {{
 *   children?: React.ReactNode,
 *   disableBorder?: boolean,
 *   roundedCorner?: boolean,
 *   showTitle?: boolean,
 * }}
 */
export let FilterBarProps;

/**
 * @param {FilterBarProps} props
 * @return {React.ReactElement}
 */
export function FilterBar({
  children,
  disableBorder = false,
  roundedCorner = false,
  showTitle = true,
}) {
  return (
    <div
      className={classNames(
        styles.bar,
        {
          [styles.barBorder]: !disableBorder,
          [styles.barRounded]: roundedCorner,
        },
      )}
    >
      {showTitle && <span className={styles.barTitle}>
        {yext.msg('Filters')}
      </span>}
      {children}
    </div>
  );
}

/**
 * @param {{
 *   onClick: function(): void,
 *   tid?: string,
 *   pendoId?: string,
 *   __dontusethis__hack__paddingTop?: (string|number),
 * }} props
 * @returns {React.ReactElement}
 */
export function AddFilterButton({onClick, __dontusethis__hack__paddingTop = 0, tid, pendoId}) {
  return (
    <div
      style={{paddingTop: __dontusethis__hack__paddingTop}}
      className={styles.addFilterButton}
      tid={tid}
    >
      <Pill
        label={yext.msg('+ Add Filter')}
        pendoId={pendoId}
        onClick={onClick}
        isDefaultValue={false}
      />
    </div>
  );
}

/**
 * @param {{
 *   onClick: (function():void),
 *   label?: string,
 *   pendoId?: string,
 * }} props
 * @returns {React.ReactElement}
 */
export function ClearFiltersButton({onClick, label = yext.msg('Clear'), pendoId}) {
  return (
    <div className={styles.clearFiltersButton}>
      <Button chromeless onClick={onClick} pendoId={pendoId}>
        {label}
      </Button>
    </div>
  );
}

/**
 * A mobile-friendly version of FilterBar
 * @param {{
 *   children?: React.ReactNode,
 *   bottomBorder?: boolean,
 * }} props
 * @return {React.ReactElement}
 */
export function ResponsiveFilterBar({children, bottomBorder = false}) {
  return (
    <div className={classNames(
      styles.responsiveBar,
      {
        [styles.bottomBorder]: bottomBorder,
      },
    )}>
      {children}
    </div>
  );
}

/**
 * A mobile-friendly version of Section
 * @param {{children?: React.ReactNode}} props
 * @return {React.ReactElement}
 */
export function ResponsiveSection({children}) {
  return <div className={styles.responsiveSection}>{children}</div>;
}
